import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { fontIndivisibleVariable, theme } from "shared/lib/styles";

import { ContentItemPostMagazinePrimary } from "scmp-app/components/content/content-item-render/variants/post-magazine-primary";
import {
  CoverImage,
  StyledCoverEntityLink,
  StyledEntityLink,
} from "scmp-app/components/content/content-item-render/variants/post-magazine-primary/styles";
import { EntityLink } from "scmp-app/components/entity-link";

export type PostMagazineSectionTopWidgetOneProps = {
  $variant?: "default" | "withFiveArticle" | "withThreeArticle";
};

export type StyledContentItemPostMagazinePrimaryProps = {
  $showMobileSquareImage?: boolean;
};

export const SectionInfoWrapper = styled.div`
  grid-area: section-info;

  order: -1;

  ${theme.breakpoints.between("mobile", "desktop")} {
    display: flex;
    justify-content: space-between;
  }
`;

export const StyledSectionNameEntityLink = styled(EntityLink)``;

export const SectionName = styled.h2`
  color: #111111;
  font-size: 32px;
  font-family: ${fontIndivisibleVariable};
  font-style: normal;
  line-height: 110%;
  letter-spacing: -1.6px;
  text-transform: uppercase;
  font-variation-settings: "wght" 300;
  ${theme.breakpoints.up("desktop")} {
    font-size: 48px;
    font-style: normal;
    letter-spacing: -2.4px;
  }
`;

export const Description = styled.div`
  display: none;

  color: #111111;
  font-size: 14px;
  font-family: ${fontIndivisibleVariable};
  font-style: normal;
  line-height: 20px;
  letter-spacing: -0.28px;
  font-variant-numeric: lining-nums proportional-nums;
  font-variation-settings: "wght" 400;
  ${theme.breakpoints.up("tablet")} {
    display: block;
  }
  ${theme.breakpoints.between("tablet", "desktop")} {
    inline-size: 276px;
    padding-inline-start: 24px;
    border-inline-start: 1px solid #777777;

    box-sizing: content-box;
  }
  ${theme.breakpoints.up("desktop")} {
    margin-block-start: 40px;

    font-size: 18px;
    line-height: 26px;
    letter-spacing: -0.36px;
  }
`;

export const AdsContainer = styled.div`
  grid-area: ad-slot;

  display: flex;
  flex-flow: row;
  justify-content: center;
  align-items: center;

  overflow-x: hidden;
`;

export const Divider = styled.div`
  grid-area: divider;

  display: none;

  inline-size: 1px;
  block-size: 100%;

  background-color: #777777;
  ${theme.breakpoints.up("desktop")} {
    display: block;
  }
`;

export const StyledContentItemPostMagazinePrimary = styled(
  ContentItemPostMagazinePrimary,
)<StyledContentItemPostMagazinePrimaryProps>`
  ${props =>
    props.$showMobileSquareImage &&
    css`
      ${theme.breakpoints.only("mobile")} {
        ${StyledCoverEntityLink}, ${CoverImage} {
          aspect-ratio: 1;
        }
      }
    `}
  ${StyledEntityLink} {
    font-size: 24px;
    letter-spacing: -0.72px;

    ${theme.breakpoints.up("desktop")} {
      font-size: 32px;
      letter-spacing: -0.96px;
    }
  }
`;

const getDesktopGridLayout = (variant: PostMagazineSectionTopWidgetOneProps["$variant"]) => {
  switch (variant) {
    case "withFiveArticle":
      return css`
        grid:
          "section-info divider first-article second-article" min-content
          "section-info divider third-article fourth-article" min-content
          "ad-slot divider fifth-article sponsor-article" min-content / 300px 1px minmax(0, 390px)
          minmax(0, 390px);
      `;
    case "withThreeArticle":
      return css`
        grid:
          "section-info divider first-article first-article" min-content
          "ad-slot divider second-article third-article" min-content / 300px 1px minmax(0, 390px)
          minmax(0, 390px);
      `;
    default:
      return css`
        grid:
          "section-info divider first-article second-article" min-content
          "ad-slot divider third-article fourth-article" min-content / 300px 1px minmax(0, 390px)
          minmax(0, 390px);
      `;
  }
};

export const Container = styled.div<PostMagazineSectionTopWidgetOneProps>`
  display: flex;
  flex-direction: column;
  gap: 40px;

  margin-inline: 20px;
  padding-block-start: 40px;

  border-block-start: 1px solid #777777;
  ${theme.breakpoints.up("tablet")} {
    margin-inline: 40px;
  }

  ${StyledContentItemPostMagazinePrimary} {
    &:nth-child(1) {
      grid-area: first-article;
    }
    &:nth-child(2) {
      grid-area: second-article;
    }
    &:nth-child(3) {
      grid-area: third-article;
    }
    &:nth-child(4) {
      grid-area: fourth-article;
    }
    &:nth-child(5) {
      grid-area: fifth-article;
    }
    &:nth-child(6) {
      grid-area: sponsor-article;
    }
  }

  ${theme.breakpoints.up("tablet")} {
    display: grid;
    ${props =>
      props.$variant === "withThreeArticle"
        ? css`
            grid:
              "section-info section-info" min-content
              "first-article first-article" min-content
              "second-article third-article" min-content
              "ad-slot ad-slot" min-content
              / 1fr 1fr;
          `
        : css`
            grid:
              "section-info section-info" min-content
              "first-article second-article" min-content
              "third-article fourth-article" min-content
              "fifth-article sponsor-article" min-content
              "ad-slot ad-slot" min-content
              / 1fr 1fr;
          `};
  }

  ${theme.breakpoints.up("desktop")} {
    ${props => getDesktopGridLayout(props.$variant ?? "default")};

    ${AdsContainer} {
      align-self: self-end;
    }
  }
`;
